import { createRef } from '@wix/materializer'
import { ThemeData, ThemeFontMap, Component } from '@wix/thunderbolt-becky-types'
import type { BeckyModel } from '@wix/thunderbolt-becky-root'
import { ParsedSvg } from '@wix/thunderbolt-commons'

export const envRefs = {
	renderScrollSnap: createRef<boolean>(['environment', 'renderFlags', 'renderScrollSnap']),
	renderSticky: createRef<boolean>(['environment', 'renderFlags', 'renderSticky']),
	compsToRenderSticky: (compId: string) =>
		createRef<BeckyModel['renderFlags']['compsToRenderSticky'][string]>([
			'environment',
			'renderFlags',
			'compsToRenderSticky',
			compId,
		]),
	widgetSpecData: (appDefinitionId: string, widgetId: string) =>
		createRef<BeckyModel['clientSpecMap'][string]['widgets'][string]>([
			'environment',
			'atoms',
			'clientSpecMap',
			appDefinitionId,
			'widgets',
			widgetId,
		]),
	showScreenInComp: createRef<boolean>(['environment', 'renderFlags', 'showScreenInComp']),
	experiment: (experiment: string) =>
		createRef<boolean | string>(['environment', 'atoms', 'experiments', experiment]),
	experiments: createRef<BeckyModel['experiments']>(['environment', 'atoms', 'experiments']),
	// Where this should be?
	isMobileView: createRef<boolean>(['environment', 'mobile', 'isMobileView']),
	isNonAdaptiveMobile: createRef<boolean>(['environment', 'mobile', 'isNonAdaptiveMobile']),
	deviceType: createRef<BeckyModel['deviceType'] | undefined>(['environment', 'mobile', 'deviceType']),
	viewMode: createRef<BeckyModel['viewMode']>(['environment', 'atoms', 'viewMode']),
	formFactor: createRef<BeckyModel['formFactor']>(['environment', 'atoms', 'formFactor']),
	meshRuntimeData_absoluteComps: createRef<BeckyModel['meshRuntimeData']['absoluteComps']>([
		'environment',
		'atoms',
		'meshRuntimeData',
		'absoluteComps',
	]),
	// Make sure you are using the siteWidth dependency value catharsis like the following
	// (deps?.isMobileView && MOBILE_SITE_WIDTH) || deps?.siteWidth || DEFAULT_DESKTOP_SITE_WIDTH
	siteWidth: createRef<number>(['data', 'document_data', 'masterPage', 'renderModifiers', 'siteWidth']),
	componentViewMode: createRef<BeckyModel['renderFlags']['componentViewMode']>([
		'environment',
		'renderFlags',
		'componentViewMode',
	]),
	isWixAdsAllowed: createRef<BeckyModel['renderFlags']['isWixAdsAllowed']>([
		'environment',
		'renderFlags',
		'isWixAdsAllowed',
	]),
	ooiCssConfig: (widgetId: string) =>
		createRef<BeckyModel['ooiCssConfigStore'][string]>(['environment', 'ooiCssConfigStore', widgetId]),
	ooiWidgetsAppSettings: (widgetId: string, referenceId: string | null) =>
		createRef<BeckyModel['ooiWidgetsAppSettings'][string] | undefined>([
			'environment',
			'atoms',
			'ooiWidgetsAppSettings',
			`${widgetId}-${referenceId}`,
		]),
	shouldRenderWixAdsOnNavigation: createRef<BeckyModel['shouldRenderWixAdsOnNavigation']>([
		'environment',
		'atoms',
		'shouldRenderWixAdsOnNavigation',
	]),
	enableVariantsTransitionsInEditor: createRef<BeckyModel['enableVariantsTransitionsInEditor']>([
		'environment',
		'atoms',
		'enableVariantsTransitionsInEditor',
	]),
	compIdToIgnoredTransformProps: (compId: string) =>
		createRef<BeckyModel['compIdToIgnoredTransformProps'][string]>([
			'environment',
			'compIdToIgnoredTransformProps',
			compId,
		]),
	ignoredTransformPropsForType: (type: Component['type']) =>
		createRef<BeckyModel['ignoredTransformPropsForType'][Component['type']]>([
			'environment',
			'ignoredTransformPropsForType',
			type,
		]),
	globalIgnoredTransformProps: createRef<BeckyModel['globalIgnoredTransformProps']>([
		'environment',
		'atoms',
		'globalIgnoredTransformProps',
	]),
	publicTopology: createRef<BeckyModel['publicTopology']>(['environment', 'atoms', 'publicTopology']),
	qaMode: createRef<BeckyModel['qaMode']>(['environment', 'atoms', 'qaMode']),
	isInSeo: createRef<BeckyModel['isInSeo']>(['environment', 'atoms', 'isInSeo']),
	language: createRef<BeckyModel['language']>(['environment', 'atoms', 'language'], 'en'),
	metaSiteId: createRef<BeckyModel['metaSiteId']>(['environment', 'atoms', 'metaSiteId']),
	externalBaseUrl: createRef<BeckyModel['externalBaseUrl']>(['environment', 'atoms', 'externalBaseUrl']),
	parsedSvg: (svgId: string) => createRef<ParsedSvg>(['svgStore', 'parsedSvg', svgId]),
	activeVariant: createRef<string>(['environment', 'atoms', 'activeVariant']),
	activeBreakpoints: createRef<Record<string, boolean>>(['environment', 'atoms', 'activeBreakpoints']),
	stylableInstance: createRef(['environment', 'atoms', 'stylableInstance']),
	isWixSite: createRef<BeckyModel['isWixSite']>(['environment', 'atoms', 'isWixSite']),
	editorName: createRef<BeckyModel['editorName']>(['environment', 'atoms', 'editorName']),
	isResponsive: createRef<BeckyModel['isResponsive']>(['environment', 'atoms', 'isResponsive']),
	isTrackClicksAnalyticsEnabled: createRef<BeckyModel['isTrackClicksAnalyticsEnabled']>([
		'environment',
		'atoms',
		'isTrackClicksAnalyticsEnabled',
	]),
	transparentComponentsOverlay: createRef<BeckyModel['renderFlags']['transparentComponentsOverlay']>([
		'environment',
		'renderFlags',
		'transparentComponentsOverlay',
	]),
	isLivePreviewOpenForEditor: createRef<BeckyModel['renderFlags']['isLivePreviewOpenForEditor']>([
		'environment',
		'renderFlags',
		'isLivePreviewOpenForEditor',
	]),
	isAdi: createRef<BeckyModel['renderFlags']['isAdi']>(['environment', 'renderFlags', 'isAdi']),
	shouldShowCoBrandingBanner: createRef<BeckyModel['shouldShowCoBrandingBanner']>([
		'environment',
		'atoms',
		'shouldShowCoBrandingBanner',
	]),
	isOneDocMigrated: createRef<BeckyModel['isOneDocMigrated']>(['environment', 'atoms', 'isOneDocMigrated']),
	anywhereThemeOverride: createRef<BeckyModel['anywhereThemeOverride']>([
		'environment',
		'atoms',
		'anywhereThemeOverride',
	]),
	migratingToOoiWidgetIds: createRef<BeckyModel['migratingToOoiWidgetIds']>([
		'environment',
		'atoms',
		'migratingToOoiWidgetIds',
	]),
}

export const themeRefs = {
	themeFonts: createRef<ThemeFontMap>(['data', 'theme_data', 'THEME_DATA', 'fontMap']),
	siteColors: createRef<ThemeData['color']>(['data', 'theme_data', 'THEME_DATA', 'color']),
	siteFonts: createRef<ThemeData['font']>(['data', 'theme_data', 'THEME_DATA', 'font']),
	fontColorMap: createRef<ThemeData['fontColorMap']>(['data', 'theme_data', 'THEME_DATA', 'fontColorMap']),
	siteFontDisplay: createRef<ThemeData['fontDisplay']>(['data', 'theme_data', 'THEME_DATA', 'fontDisplay']),
	theme: createRef<ThemeData['theme']>(['data', 'theme_data', 'THEME_DATA', 'theme']),
	textTheme: createRef<ThemeData['textTheme']>(['data', 'theme_data', 'THEME_DATA', 'textTheme']),
}

export const qaRefs = {
	fullNameCompType: (id: string) => createRef<string>(['QA', 'fullNameCompType', id]),
}
